import { plural, t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { TireQuoteRecordCustomer, type TireQuoteRejectionReason } from '@navio/nsdk';
import { invalidateQueryExactMatch, noddiAsync, URLKeys } from 'noddi-async';
import { NavioOverlayActions, NoddiButton, NoddiDialog, noddiToast } from 'noddi-ui-web';

import { useState } from 'react';
import { ApiErrorMessageWithTrans } from '../../../../components/Error/ApiErrorMessageWithTrans';
import { RejectionReasonPicker } from '../../../../components/TireOffer/RejectionReasonPicker';

type DeclineModalProps = {
  open: boolean;
  offers: TireQuoteRecordCustomer[];
  userGroupId: number;
  onCancel: () => void;
  onConfirm: () => void;
};

export const DeclineModal = ({ open, offers, userGroupId, onCancel, onConfirm }: DeclineModalProps) => {
  const [reasons, setReasons] = useState<TireQuoteRejectionReason[]>([]);

  const {
    mutateAsync: postRejectTireOfferQuote,
    isPending,
    error
  } = noddiAsync.usePost({ type: URLKeys.postRejectTireOfferQuote });

  const count = offers.length;

  const handleConfirm = async () => {
    const results = await Promise.allSettled(
      offers.map((offer) => postRejectTireOfferQuote({ carTireQuoteId: offer.id, reasons }))
    );

    const failed = results.filter((result) => result.status === 'rejected').length;
    const succeeded = count - failed;
    invalidateQueryExactMatch({
      urlKey: URLKeys.getCarTireQuotesIgnoringImpersonation,
      input: { userGroupIds: [userGroupId] }
    });

    if (succeeded > 0) {
      noddiToast.success(plural(succeeded, { one: '# offer declined', other: '# offers declined' }));
    }
    if (failed > 0) {
      noddiToast.error(plural(failed, { one: 'Failed to decline # offer', other: 'Failed to decline # offers' }));
    }
    onConfirm();
    setReasons([]);
  };

  return (
    <NoddiDialog
      title={plural(count, { one: 'Decline # offer', other: 'Decline # offers' })}
      description={t`These offers will be marked as declined and removed from your pending list.`}
      open={open}
      onClose={onCancel}
      maxWidth='sm'
      footer={
        <NavioOverlayActions>
          <NoddiButton variant='outline' onClick={onCancel} disabled={isPending}>
            <Trans>Cancel</Trans>
          </NoddiButton>
          {/* oxlint-disable-next-line typescript/no-misused-promises -- async onClick runs handleConfirm(); React discards the returned promise, so a rejection is unhandled unless the body catches */}
          <NoddiButton variant='destructive' onClick={handleConfirm} loading={isPending}>
            {plural(count, { one: 'Decline offer', other: 'Decline offers' })}
          </NoddiButton>
        </NavioOverlayActions>
      }
    >
      <div className='flex flex-col gap-3 px-1 pb-2'>
        <div className='text-xs font-medium text-muted-foreground'>
          <Trans>Reasons (optional)</Trans>
        </div>
        <RejectionReasonPicker value={reasons} onChange={setReasons} />
        {error && <ApiErrorMessageWithTrans error={error} />}
      </div>
    </NoddiDialog>
  );
};
